import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import StandardEmpty from "../../empty/StandardEmpty";

function BaseCarousel({
  title,
  items = [],
  isLoading,
  seeAllPath,
  emptyMsg,
  seeAllText,
  previousAlt,
  nextAlt,
  renderItem,
  GhostComponent,
  showBorder = true,
  showArrows = true,
  showDots = true,
  containerClassName = "",
  titleClassName = "",
  description = "",
}) {
  const scrollRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  /**
   * Helper to fetch card elements
   */
  const getCards = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return [];
    const wrapper = container.querySelector(".scc-body-wrapper");
    if (!wrapper) return [];
    return Array.from(wrapper.children);
  }, []);

  /**
   * Calculates page metrics:
   * - cardsPerPage
   * - pageStep (scroll distance per page)
   * - totalPages
   */
  const getPageMetrics = useCallback(() => {
    const container = scrollRef.current;
    const cards = getCards();

    if (!container || !cards.length) {
      return { cardsPerPage: 1, pageStep: 0, totalPages: 0 };
    }

    const containerWidth = container.clientWidth;
    const firstCard = cards[0];
    const cardWidth = firstCard.offsetWidth;

    // Extract gap from wrapper styles (defaults to 12px if unavailable)
    const wrapper = container.querySelector(".scc-body-wrapper");
    const gap = wrapper
      ? parseInt(window.getComputedStyle(wrapper).gap || "12", 10)
      : 12;

    const cardStep = cardWidth + gap;
    const cardsPerPage = Math.max(
      1,
      Math.floor((containerWidth + gap) / cardStep),
    );
    const pageStep = cardsPerPage * cardStep;
    const calculatedPages = Math.ceil(cards.length / cardsPerPage);

    return { cardsPerPage, pageStep, totalPages: calculatedPages };
  }, [getCards]);

  /**
   * Updates state for arrows, active page dot, and total dots
   */
  const updateCarouselState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = Math.max(
      0,
      container.scrollWidth - container.clientWidth,
    );

    // Update Arrows
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft < maxScrollLeft - 2);

    const { pageStep, totalPages: calculatedPages } = getPageMetrics();
    setTotalPages(calculatedPages);

    if (isProgrammaticScrollRef.current) return;

    // Check if scrolled to the very end
    if (scrollLeft >= maxScrollLeft - 5 && calculatedPages > 0) {
      setActivePageIndex(calculatedPages - 1);
      return;
    }

    // Determine current active page index based on pageStep distance
    if (pageStep > 0) {
      const currentPage = Math.round(scrollLeft / pageStep);
      setActivePageIndex(
        Math.max(0, Math.min(currentPage, calculatedPages - 1)),
      );
    }
  }, [getPageMetrics]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    updateCarouselState();

    const resizeObserver = new ResizeObserver(() => {
      updateCarouselState();
    });

    resizeObserver.observe(container);

    const wrapper = container.querySelector(".scc-body-wrapper");
    if (wrapper) {
      resizeObserver.observe(wrapper);
      Array.from(wrapper.children).forEach((card) => {
        resizeObserver.observe(card);
      });
    }

    return () => resizeObserver.disconnect();
  }, [items, isLoading, updateCarouselState]);

  const handleScroll = useCallback(() => {
    updateCarouselState();
  }, [updateCarouselState]);

  /**
   * Scrolls directly to a target page index
   */
  const scrollToPage = useCallback(
    (pageIndex) => {
      const container = scrollRef.current;
      const { pageStep, totalPages: maxPages } = getPageMetrics();

      if (!container || !pageStep) return;

      const safePageIndex = Math.max(0, Math.min(pageIndex, maxPages - 1));
      const maxScrollLeft = Math.max(
        0,
        container.scrollWidth - container.clientWidth,
      );

      // Target position aligned to card start, capped at maxScrollLeft
      const targetScrollLeft = Math.min(
        safePageIndex * pageStep,
        maxScrollLeft,
      );

      setActivePageIndex(safePageIndex);
      isProgrammaticScrollRef.current = true;

      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });

      window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        updateCarouselState();
      }, 400);
    },
    [getPageMetrics, updateCarouselState],
  );

  /**
   * Arrow navigation shifts by 1 page unit
   */
  const handleArrowNavigation = useCallback(
    (direction) => {
      if (direction === "next") {
        if (activePageIndex < totalPages - 1) {
          scrollToPage(activePageIndex + 1);
        }
      } else {
        if (activePageIndex > 0) {
          scrollToPage(activePageIndex - 1);
        }
      }
    },
    [activePageIndex, totalPages, scrollToPage],
  );

  if (isLoading) {
    return (
      <div
        className={`standard-carousel-container ${containerClassName}`}
        style={!showBorder ? { border: 0, padding: 0 } : {}}
      >
        <div className="scc-header">
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <h4
              className={`carousel-title ${titleClassName}`}
              style={{ margin: 0 }}
            >
              {title}
            </h4>
            <p>{description}</p>
          </div>
          <div className="scc-arrows" style={{ opacity: 0.2 }}>
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "#eee",
              }}
            />
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "#eee",
              }}
            />
          </div>
        </div>

        {GhostComponent}

        {showDots && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "6px",
              marginTop: "16px",
            }}
          >
            <div
              style={{
                width: "14px",
                height: "6px",
                borderRadius: "9999px",
                background: "#eee",
              }}
            />
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "#eee",
              }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`standard-carousel-container ${containerClassName}`}
      style={!showBorder ? { border: 0, padding: 0 } : {}}
    >
      {/* HEADER */}
      <div className="scc-header">
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <h4
            className={`carousel-title ${titleClassName}`}
            style={{ margin: 0 }}
          >
            {title}
          </h4>
          <p>{description}</p>
        </div>

        {(showArrows || seeAllPath) && (
          <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
            {showArrows && (
              <div className="scc-arrows">
                {/* PREVIOUS */}
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/arrow_down.svg`}
                  alt={previousAlt || ""}
                  className="arrow-carousel arrow-left"
                  onClick={() => {
                    if (canScrollLeft) {
                      handleArrowNavigation("prev");
                    }
                  }}
                  style={{
                    cursor: canScrollLeft ? "pointer" : "default",
                    opacity: canScrollLeft ? 1 : 0.2,
                  }}
                />

                {/* NEXT */}
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/arrow_down.svg`}
                  alt={nextAlt || ""}
                  className="arrow-carousel arrow-right"
                  onClick={() => {
                    if (canScrollRight) {
                      handleArrowNavigation("next");
                    }
                  }}
                  style={{
                    cursor: canScrollRight ? "pointer" : "default",
                    opacity: canScrollRight ? 1 : 0.2,
                  }}
                />
              </div>
            )}

            {seeAllPath && (
              <Link to={seeAllPath} className="see-all-standard rounded">
                {seeAllText}
              </Link>
            )}
          </div>
        )}
      </div>

      {/* CAROUSEL BODY */}
      <div className="scc-body" ref={scrollRef} onScroll={handleScroll}>
        {items.length > 0 ? (
          <div className="scc-body-wrapper">
            {items.map((item, index) => (
              <div className="scc-carousel-item" key={item?.id ?? index}>
                {renderItem(item, index)}
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              padding: "40px 20px",
            }}
          >
            <StandardEmpty msg={emptyMsg} />
          </div>
        )}
      </div>

      {/* PAGINATION */}
      {showDots && totalPages > 1 && (
        <div
          className="scc-pagination-dots"
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {Array.from({ length: totalPages }).map((_, index) => {
            const isActive = index === activePageIndex;

            return (
              <div
                key={index}
                onClick={() => scrollToPage(index)}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  width: isActive ? "14px" : "6px",
                  height: "6px",
                  borderRadius: "9999px",
                  backgroundColor: isActive
                    ? "var(--primary-500, #004789)"
                    : "#D1D1D1",
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BaseCarousel;
